/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react'

import { map } from 'lodash'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { fetchPopularDestinations } from 'actions/cms/popularDestinations'
import Tabs from 'components/tabs-new'
import { TAB_TITLES } from 'constants/popularDestinations'
import { isEmpty, isMobile } from 'helpers/utils'
import NAMESPACES from 'localization/constants/namespaces'
import {
  isFetchingPopularDestinations,
  getPopularDestinationsError,
  selectPopularDestinationsFromSource
} from 'selectors/cms/popularDestinations'
import { mapMediaType } from 'selectors/mediaType'

import DestinationThumbnail from './destination-thumbnail'
import { Container, ContentWrapper, StyledTab, Panel, PanelContainer } from './styled'

const PopularDestinations = ({
  isFetching,
  popularDestinations,
  fetchPopularDestinations,
  mobile,
  isOnLandingPage,
  error
}) => {
  const { t } = useTranslation(NAMESPACES.HOME)

  useEffect(() => {
    !isOnLandingPage && isEmpty(popularDestinations) && fetchPopularDestinations()
  }, [])

  const getTabsAndContent = () => {
    if (!isEmpty(popularDestinations)) {
      const tabs = map(popularDestinations, (value, key) => {
        return { title: t(TAB_TITLES[key]) }
      })

      // only allows 4 items by tab
      const content = map(popularDestinations, value => getContent(value.slice(0, 4)))

      return { tabs, content }
    }
  }

  const getContent = items => {
    let content = []

    // render mobile items without highlight prop
    if (mobile && items.some(item => !item.highlighted)) {
      const someMobileItems = items
        .filter(item => item.highlighted)
        .map((item, index) => getDestinationsMobile(item, index))
      !isEmpty(someMobileItems) && content.push(someMobileItems)
    }

    // render desktop items or mobile items with highlight
    const filteredItems = mobile ? items.filter(item => !item.highlighted) : items
    content.push(getDestinations(filteredItems))

    return <PanelContainer key="panel-container">{content}</PanelContainer>
  }

  const getDestinationsMobile = (item, index) => (
    <Panel key={index}>
      <DestinationThumbnail item={item} index={index} />
    </Panel>
  )

  const getDestinations = items => {
    const total = items.length

    return (
      <Panel scrollable fixHeightOnIosMobile key="items">
        {items.map((item, index) => (
          <DestinationThumbnail
            key={index}
            index={index}
            item={item}
            highlighted={!mobile && (total < 3 || (total === 3 && index === 0))}
          />
        ))}
      </Panel>
    )
  }

  if (isFetching || isEmpty(popularDestinations) || error) {
    return null
  }

  const tabsAndContent = getTabsAndContent()

  if (Object.values(popularDestinations).length === 1 && isOnLandingPage) {
    return <Container>{tabsAndContent && tabsAndContent.content}</Container>
  }

  return (
    <Container>
      <ContentWrapper>
        <Tabs {...tabsAndContent} TabComponent={<StyledTab />} barWidth="100%" />
      </ContentWrapper>
    </Container>
  )
}

const mapStateToProps = state => ({
  popularDestinations: selectPopularDestinationsFromSource(state),
  isFetching: isFetchingPopularDestinations(state),
  mobile: isMobile(mapMediaType(state)),
  error: getPopularDestinationsError(state)
})

const mapDispatchToProps = { fetchPopularDestinations }

PopularDestinations.propTypes = {
  popularDestinations: PropTypes.object.isRequired,
  isFetching: PropTypes.bool,
  mediaType: PropTypes.string,
  fetchPopularDestinations: PropTypes.func,
  error: PropTypes.object,
  isOnLandingPage: PropTypes.bool
}

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(PopularDestinations))
